import { useEffect } from "react";
import type { ErrorEvent, EventHint, Log } from "@sentry/core";
import {
  browserProfilingIntegration,
  consoleLoggingIntegration,
  init,
  reactRouterV6BrowserTracingIntegration,
  replayIntegration,
} from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { redactStructuredData } from "./sensitive-data";

let didInit = false;

function getDsn(): string | undefined {
  const raw = import.meta.env.VITE_SENTRY_DSN;
  if (typeof raw !== "string") return undefined;
  const t = raw.trim();
  return t.length > 0 ? t : undefined;
}

export function isSentryConfigured(): boolean {
  return Boolean(getDsn());
}

function buildTracePropagationTargets(): (string | RegExp)[] {
  /**
   * Do not include `*.supabase.co` here: trace propagation injects `baggage` /
   * `sentry-trace`, and Supabase (REST + Edge Functions) CORS allowlists omit
   * those headers → browser preflight fails on `/functions/v1/*` etc.
   * Spans for Supabase fetches still work; only cross-service header propagation is omitted.
   */
  const targets: (string | RegExp)[] = [
    /^https?:\/\/localhost(:\d+)?/i,
    /^https?:\/\/127\.0\.0\.1(:\d+)?/i,
  ];

  const apiOrigin = import.meta.env.VITE_API_ORIGIN;
  if (typeof apiOrigin === "string" && apiOrigin.length > 0) {
    try {
      targets.push(new URL(apiOrigin).origin);
    } catch {
      /* skip */
    }
  }

  return targets;
}

function resolveEnvironment(): string {
  const override = import.meta.env.VITE_SENTRY_ENVIRONMENT;
  if (typeof override === "string" && override.trim().length > 0) {
    return override.trim();
  }
  return import.meta.env.MODE;
}

function stripSensitiveRequestPayload(event: ErrorEvent): void {
  try {
    const req = event.request;
    if (!req?.data || typeof req.data !== "object") return;

    const redacted = redactStructuredData(req.data);
    if (redacted && typeof redacted === "object" && !Array.isArray(redacted)) {
      req.data = redacted as typeof req.data;
    }
  } catch {
    /* Never drop Issues if redaction touches an unexpected shape */
  }
}

/**
 * Applies PII-conscious redaction. Wrapped so a bad payload never suppresses Issues.
 */
function beforeSend(event: ErrorEvent, _hint: EventHint): ErrorEvent | null {
  try {
    if (event.extra) {
      event.extra = redactStructuredData(event.extra) as typeof event.extra;
    }
    if (event.contexts) {
      event.contexts = redactStructuredData(event.contexts) as typeof event.contexts;
    }
    if (event.breadcrumbs?.length) {
      event.breadcrumbs = event.breadcrumbs.map((b) => ({
        ...b,
        data: b.data ? (redactStructuredData(b.data) as typeof b.data) : b.data,
      }));
    }
    stripSensitiveRequestPayload(event);
    return event;
  } catch {
    return event;
  }
}

function beforeSendLog(log: Log): Log | null {
  try {
    if (log.attributes) {
      return {
        ...log,
        attributes: redactStructuredData(log.attributes) as Log["attributes"],
      };
    }
    return log;
  } catch {
    return log;
  }
}

function resolveSendDefaultPii(): boolean {
  return import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII === "true";
}

function resolveSentryDebug(): boolean {
  return (
    import.meta.env.DEV && import.meta.env.VITE_SENTRY_DEBUG === "true"
  );
}

/**
 * Call once before React render. No-ops when `VITE_SENTRY_DSN` is unset.
 */
export function initSentry(): void {
  const dsn = getDsn();
  if (!dsn || didInit) {
    return;
  }
  didInit = true;

  const isProd = import.meta.env.PROD;
  const tracesSampleRate = isProd ? 0.2 : 1.0;
  /** Session-level gate for browser JS profiling (UI profiler / “Profiling V2”) */
  const profileSessionSampleRate = isProd ? 0.1 : 1.0;
  const replaysSessionSampleRate = isProd ? 0.1 : 1.0;
  const replaysOnErrorSampleRate = 1.0;

  const release =
    typeof import.meta.env.VITE_SENTRY_RELEASE === "string" &&
    import.meta.env.VITE_SENTRY_RELEASE.trim().length > 0
      ? import.meta.env.VITE_SENTRY_RELEASE.trim()
      : undefined;

  init({
    dsn,
    environment: resolveEnvironment(),
    release,
    /*
     * Sentry Logs (console + Sentry.logger) appear under Logs / Explore.
     * Issues / Performance come from exceptions, captureMessage(...), and spans.
     */
    sendDefaultPii: resolveSendDefaultPii(),
    debug: resolveSentryDebug(),
    enableLogs: true,
    /** Sentry Metrics (count / gauge / distribution) — `@sentry/react` ≥ 10.25 */
    enableMetrics: true,
    integrations: [
      /*
       * `reactRouterV6BrowserTracingIntegration` wraps `browserTracingIntegration` — do not add both.
       */
      reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      browserProfilingIntegration(),
      replayIntegration({
        maskAllText: false,
        blockAllMedia: true,
        mask: ['input[type="password"]', "[data-sentry-mask]"],
      }),
      consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    tracesSampleRate,
    tracePropagationTargets: buildTracePropagationTargets(),
    /** Profiles collected with traced root spans (`profileLifecycle: 'trace'` + browser profiler). Requires `Document-Policy: js-profiling` on the HTML response. */
    profileLifecycle: "trace",
    profileSessionSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    ignoreErrors: [
      /^ResizeObserver loop limit exceeded/i,
      /^ResizeObserver loop completed with undelivered notifications/i,
      /^Non-Error promise rejection captured with value: Object Not Found Matching/i,
      /^AbortError: The user aborted a request/i,
      /^CanceledError/i,
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-web-extension:\/\//i,
    ],
    beforeSend,
    beforeSendLog,
  });
}
